import { Cookie as CookieNames } from '@ps/web-core-enums';
import { canUseDOM, getOrGenerateInstallId, getWindow } from '@ps/web-core-utils';
import JWTDecode from 'jwt-decode';
import Cookies from 'universal-cookie';
import type { Required } from 'utility-types';

import { select } from '../../../index';
import type { Model, RootState, SelectorCreator, Slicer } from '../../types';

export type IdpInfo = 'idpToken' | 'idpRefreshToken' | 'idpTokenExpires';

export interface SessionState {
  authSource: 'client' | 'server';
  installId?: string;
  loginSource?: LoginSource;
  serverAuth?: boolean;
  token?: string;
  idpToken?: string;
  idpRefreshToken?: string;
  idpTokenExpires?: number;
}

export enum LoginSource {
  Login = 'login',
  RefreshSession = 'refreshSession',
  Registration = 'registration',
  VerifySession = 'verifySession'
}

export type AuthenticatedSession = Required<SessionState, 'loginSource'>;

export type SessionResponse = Omit<AuthenticatedSession, 'serverAuth' | 'authSource' | 'installId'>;

const cookies = new Cookies();

const getState = (): SessionState => {
  const authSource = 'server';
  const installId = getOrGenerateInstallId();

  return getWindow() ? { installId, authSource } : { authSource };
};

const reducers = {
  response: (state: SessionState, payload: SessionResponse): SessionState => ({
    ...state,
    ...getState(),
    ...payload,
    authSource: 'client'
  }),
  update: (
    state: SessionState,
    payload: Partial<Pick<SessionState, 'serverAuth' | 'authSource' | 'token' | IdpInfo>>
  ): SessionState => ({
    ...getState(),
    ...state,
    ...payload
  }),
  clear: ({ installId }: SessionState): SessionState => ({
    authSource: 'client',
    installId: installId ?? getState().installId
  }),
  'persist/REHYDRATE': (
    state: SessionState,
    payload: { session: Partial<SessionState> } = { session: {} }
  ) => ({
    ...state,
    ...payload.session
  })
};

const selectors = (slice: Slicer<SessionState>, createSelector: SelectorCreator) => ({
  get() {
    return createSelector(
      slice,
      () => (canUseDOM ? window?.starsweb?.auth.idpToken : undefined),
      (rootState: RootState) => select.session.getInstallId(rootState),
      (state, windowIdp, installId) => ({
        ...state,
        idpToken: windowIdp ? windowIdp : state.idpToken,
        installId
      })
    );
  },
  getInstallId() {
    return createSelector(
      slice,
      () => getWindow()?.starsweb?.app?.installId,
      (state, appInstallId) => (appInstallId ? appInstallId : state.installId)
    );
  },
  getJWTValues() {
    return createSelector(slice, ({ token }) => {
      try {
        return JWTDecode(token as string) as { token: string; webId: string };
      } catch {
        return;
      }
    });
  },
  getLegacySession() {
    return createSelector(
      (rootState: RootState) => select.session.getJWTValues(rootState),
      (JWTValues) => ({
        token: JWTValues?.token,
        signature: cookies.get(CookieNames.SIGNATURE),
        userWebId: JWTValues?.webId
      })
    );
  },
  getWMTAuthorization() {
    return createSelector(
      (rootState: RootState) => select.session.get(rootState),
      (rootState: RootState) => select.cms.isExternalPartnerSite(rootState),
      ({ token, idpToken }, isExtSite) => {
        if (isExtSite && idpToken) return `Bearer ${idpToken}`;
        if (token) return `StarsWeb-Session ${token}`;
      }
    );
  },
  getWebId() {
    return createSelector(
      (rootState: RootState) => select.session.getLegacySession(rootState),
      ({ userWebId }) => userWebId
    );
  },
  hasClientSession() {
    return slice(({ authSource }) => authSource === 'client');
  },
  /**
   * Determines if the client has a validated session token.
   * Will always be false for SSR and for first client render
   */
  isClientAuthenticated() {
    return createSelector(
      (rootState: RootState) => select.session.getLegacySession(rootState),
      (rootState: RootState) => select.session.get(rootState),
      (rootState: RootState) => select.account.getUserId(rootState),
      ({ token }, { idpToken }, userId) => !!((token || idpToken) && userId)
    );
  },
  /**
   * Determines if the user has a session token, which may not be validated.
   * May be true for SSR and first client render
   * Use "isClientAuthenticated" if you need to know the session has been validated.
   */
  isAuthenticated() {
    return createSelector(
      slice,
      (rootState: RootState) => select.session.isClientAuthenticated(rootState),
      (rootState: RootState) => !!select.account.getUserIntId(rootState),
      ({ serverAuth }, clientAuth, hasUserIntId) => serverAuth || (clientAuth && hasUserIntId)
    );
  },
  isAuthenticationCheckComplete() {
    return createSelector(
      slice,
      (rootState: RootState) => select.session.isClientAuthenticated(rootState),
      ({ serverAuth }, clientAuth) => serverAuth === clientAuth
    );
  },
  isFirstAuthenticatedSession() {
    return slice(({ loginSource }) => loginSource === LoginSource.Registration);
  },
  isServerAuthenticated() {
    return slice(({ serverAuth, authSource }) => !!serverAuth && authSource === 'server');
  }
});

const session: Model<SessionState, typeof reducers, void, void, typeof selectors> = {
  state: getState(),
  reducers,
  selectors
};

export default session;
